import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Labelbox } from "../../components";
import API from "../../utils/api";
import { Toaster, timeFormatter } from "../../utils/common";
import {
  MAX_LOGIN_CYCLES,
  MAX_PAIRS_FOR_VIEW,
  canPunchOnCalendarDay,
  computeMinutesWorked,
  computeSessionTimerFromEvents,
  countCompletedLogoutCycles,
  countLoginEvents,
  formatElapsedHMS,
  formatHHMM,
  getLocalYMD,
  getWorkingHoursTimeZone,
  isLoginEventType,
  isLogoutEventType,
  normalizeEvents,
  resolveRowEvents,
  resolveWorkDateYmd,
  rowHasOpenLogin,
} from "../../utils/workingHoursPunch";
import { hasHrmsAdminAccess } from "../../utils/hrmsAccess";
import {
  getSavedWorkTimeZone,
  persistWorkTimeZonePreference,
  WORK_TIMEZONE_OPTIONS,
} from "../../utils/workTimezonePreference";
import "../../components/layout/logoutModal.css";
import "./working_hours.scss";

const maxPairsForView = MAX_PAIRS_FOR_VIEW;

const WorkingHours = () => {
  const role = (localStorage.getItem("user_role") || "employee").toLowerCase();
  const currentUserId = localStorage.getItem("user_id");
  const isAdmin = hasHrmsAdminAccess(currentUserId);
  // Allowlisted HRMS admins are treated as admin, not manager-scoped.
  const isManager = role === "manager" && !isAdmin;

  const [tab, setTab] = useState(isAdmin ? "team" : "my");
  const [mode, setMode] = useState("Office");
  const [loading, setLoading] = useState(false);
  const [punching, setPunching] = useState(false);
  const [rows, setRows] = useState([]);
  const [localPunchesByDate, setLocalPunchesByDate] = useState({});
  const [range, setRange] = useState({ from: "", to: "" });
  const [filterMode, setFilterMode] = useState("All");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [workTimezone, setWorkTimezone] = useState(() => getSavedWorkTimeZone());

  const normalizeRow = useCallback(
    (r) => {
      const date = r?.date || r?.work_date || r?.workDate;
      const first_login_at =
        r?.first_login_at || r?.firstLoginAt || r?.first_login || r?.firstLogin;
      const employee_id =
        r?.employee_id || r?.employeeId || r?.emp_id || r?.user_id || r?.userId;
      // Keep every punch stored on this work day. Filtering by the viewer's
      // clock date hides logouts that fall after midnight in another timezone.
      let events = normalizeEvents(
        r?.events || r?.working_events || r?.logs,
      );
      if (!events.length) {
        events = resolveRowEvents(r);
      }

      const local = date ? normalizeEvents(localPunchesByDate[date]) : [];
      if (local.length) {
        const seen = new Set();
        const merged = [...events, ...local]
          .filter((e) => e?.type && e?.at)
          .sort((a, b) => new Date(a.at) - new Date(b.at))
          .filter((e) => {
            const key = `${String(e.type).toLowerCase()}|${new Date(e.at).toISOString()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        events = merged;
      }

      const totalMinutesNum =
        Number(r?.total_minutes ?? r?.totalMinutes ?? 0) || 0;

      return {
        ...r,
        date: date || getLocalYMD(new Date()),
        employee_id,
        first_login_at,
        events,
        total_minutes: totalMinutesNum,
      };
    },
    [localPunchesByDate],
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint =
        (isManager || isAdmin) && tab === "team"
          ? isAdmin
            ? "/working-hours/all"
            : "/working-hours/team"
          : "/working-hours/my";
      const params = new URLSearchParams();
      if (range.from) params.set("from", range.from);
      if (range.to) params.set("to", range.to);
      const qs = params.toString();
      const { data } = await API.get(`${endpoint}${qs ? `?${qs}` : ""}`);
      const list = data?.data || data || [];
      const normalized = Array.isArray(list) ? list.map(normalizeRow) : [];
      const filtered =
        isManager && tab === "team" && currentUserId
          ? normalized.filter(
              (row) =>
                !row.employee_id ||
                String(row.employee_id) !== String(currentUserId),
            )
          : normalized;
      setRows(filtered);
    } catch (e) {
      setRows([]);
      Toaster.error("Failed to load working hours");
    } finally {
      setLoading(false);
    }
  }, [
    currentUserId,
    isAdmin,
    isManager,
    normalizeRow,
    range.from,
    range.to,
    tab,
  ]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const employeeFilterOptions = useMemo(() => {
    const byId = new Map();
    for (const row of rows) {
      const id = String(row?.employee_id || "").trim();
      if (!id || byId.has(id)) continue;
      byId.set(
        id,
        row.employee_name || row.employee_email || "Employee",
      );
    }
    return [
      { id: "", value: "All Employees" },
      ...[...byId.entries()]
        .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
        .map(([id, value]) => ({ id, value })),
    ];
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows.filter((r) => {
      if (
        filterMode &&
        filterMode !== "All" &&
        String(r.mode || "").toLowerCase() !== filterMode.toLowerCase()
      ) {
        return false;
      }
      if (
        employeeFilter &&
        String(r.employee_id || "") !== String(employeeFilter)
      ) {
        return false;
      }
      return true;
    });
  }, [rows, filterMode, employeeFilter]);

  const todayYmd = getLocalYMD(new Date(nowTick));

  const myToday = useMemo(() => {
    if (tab !== "my" || !Array.isArray(rows)) return null;
    const sameDay = rows.find((r) => resolveWorkDateYmd(r) === todayYmd);
    if (sameDay && rowHasOpenLogin(sameDay)) return sameDay;
    const openToday = rows.find((r) => {
      const evs = Array.isArray(r?.events) ? r.events : [];
      const last = evs[evs.length - 1];
      return isLoginEventType(last?.type) && getLocalYMD(last?.at) === todayYmd;
    });
    return openToday || sameDay || null;
  }, [rows, tab, todayYmd]);

  const todayEvents = useMemo(() => {
    const events = Array.isArray(myToday?.events) ? myToday.events : [];
    return events;
  }, [myToday]);

  const canPunch = useMemo(() => canPunchOnCalendarDay(myToday), [myToday]);
  const timezoneReady = Boolean(workTimezone);

  const handleWorkTimezoneChange = async (nextTz) => {
    if (!nextTz || nextTz === workTimezone) return;
    const { timezone } = await persistWorkTimeZonePreference(nextTz);
    if (!timezone) return;
    setWorkTimezone(timezone);
    await fetchRows();
  };

  const nextPunch = useMemo(() => {
    if (!todayEvents.length) return "login";
    const last = todayEvents[todayEvents.length - 1];
    if (isLoginEventType(last?.type)) return "logout";
    if (isLogoutEventType(last?.type)) return "login";
    return todayEvents.length % 2 === 0 ? "login" : "logout";
  }, [todayEvents]);

  const loginLimitReached = useMemo(() => {
    return (
      countCompletedLogoutCycles(todayEvents) >= MAX_LOGIN_CYCLES ||
      countLoginEvents(todayEvents) >= MAX_LOGIN_CYCLES
    );
  }, [todayEvents]);

  const { elapsedMs, activeLoginAtMs, hasOpenLoginForgotten } = useMemo(() => {
    return computeSessionTimerFromEvents(todayEvents, nowTick, {
      workDateYmd: todayYmd,
    });
  }, [todayEvents, nowTick, todayYmd]);

  const isLoggedIn = Boolean(activeLoginAtMs);

  const activeLoginEvent = useMemo(() => {
    if (!activeLoginAtMs || !todayEvents.length) return null;
    return [...todayEvents].reverse().find((e) => isLoginEventType(e?.type));
  }, [todayEvents, activeLoginAtMs]);

  useEffect(() => {
    if (activeLoginEvent?.mode) {
      setMode(activeLoginEvent.mode);
    }
  }, [activeLoginEvent]);

  // Forgotten open login (day ended) is not an active session — allow a new login.
  const effectiveNextPunch = useMemo(() => {
    if (activeLoginAtMs) return "logout";
    if (hasOpenLoginForgotten) return "login";
    return nextPunch;
  }, [activeLoginAtMs, hasOpenLoginForgotten, nextPunch]);

  useEffect(() => {
    setNowTick(Date.now());
    const id = setInterval(
      () => setNowTick(Date.now()),
      isLoggedIn ? 1000 : 15000,
    );
    return () => clearInterval(id);
  }, [isLoggedIn, activeLoginAtMs]);

  useEffect(() => {
    const rowYmd = resolveWorkDateYmd(myToday);
    if (rowYmd && rowYmd !== todayYmd) {
      fetchRows();
    }
  }, [todayYmd, myToday, fetchRows]);

  const sessionElapsedLabel = useMemo(() => {
    return formatElapsedHMS(elapsedMs);
  }, [elapsedMs]);

  const punch = async (type) => {
    if (!timezoneReady) {
      Toaster.warning(
        "Select your work timezone first. You can change it later if your work region changes.",
      );
      return;
    }
    if (!canPunch) {
      Toaster.warning("Punching is only available for today's work date");
      return;
    }
    if (type === "login" && loginLimitReached) {
      Toaster.warning(
        "You have logged in and logged out 3 times already. Further login is not allowed for today.",
      );
      return;
    }
    if (type === "login" && effectiveNextPunch !== "login") {
      Toaster.warning("You are already logged in. Please logout first.");
      return;
    }
    if (type === "logout" && effectiveNextPunch !== "logout") {
      Toaster.warning("Please login first");
      return;
    }
    setPunching(true);
    try {
      const res = await API.post("/working-hours/punch", {
        type,
        mode,
        // Work-region calendar day + IANA zone (not OS-local getDate()).
        work_date: getLocalYMD(),
        timezone: getWorkingHoursTimeZone(),
      });
      if (res?.data?.status === 0) {
        Toaster.warning(res.data.message || "Could not update working hours");
        await fetchRows();
        return;
      }

      const payload = res?.data?.data;
      const payloadDate =
        payload?.date ||
        payload?.work_date ||
        payload?.workDate ||
        getLocalYMD(new Date());
      const payloadEvents =
        payload?.events || payload?.working_events || payload?.logs || null;

      if (Array.isArray(payloadEvents) && payloadEvents.length) {
        setLocalPunchesByDate((prev) => ({
          ...(prev || {}),
          [payloadDate]: normalizeEvents(payloadEvents),
        }));
      } else {
        const nowIso = new Date().toISOString();
        const todayKey = getLocalYMD(new Date());
        setLocalPunchesByDate((prev) => {
          const next = { ...(prev || {}) };
          const arr = Array.isArray(next[todayKey]) ? next[todayKey] : [];
          next[todayKey] = [...arr, { type, at: nowIso, mode }];
          return next;
        });
      }

      await fetchRows();
    } catch (e) {
      Toaster.error("Failed to update working hours");
    } finally {
      setPunching(false);
    }
  };

  const clearFilters = () => {
    setFilterMode("All");
    setEmployeeFilter("");
    setRange({ from: "", to: "" });
  };

  const pairLoginLogout = (events) => {
    const pairs = [];
    let openLogin = null;
    for (const event of events) {
      if (isLoginEventType(event?.type)) {
        if (openLogin) pairs.push({ login: openLogin, logout: null });
        openLogin = event;
      } else if (isLogoutEventType(event?.type)) {
        pairs.push({ login: openLogin, logout: event });
        openLogin = null;
      }
    }
    if (openLogin) pairs.push({ login: openLogin, logout: null });
    return pairs;
  };

  const sendForRegularisation = async (row) => {
    try {
      await API.post("/working-hours/send-regularisation", { date: row?.date });
      Toaster.success("Sent for regularisation");
      await fetchRows();
    } catch (e) {
      Toaster.error("Failed to send for regularisation");
    }
  };

  const regulariseRow = async (row) => {
    try {
      await API.post("/working-hours/regularise", {
        date: row?.date,
        employee_id: row?.employee_id,
      });
      Toaster.success("Regularisation accepted");
      await fetchRows();
    } catch (e) {
      Toaster.error("Failed to accept regularisation");
    }
  };

  const declineRegularisation = async (row) => {
    try {
      await API.post("/working-hours/decline-regularisation", {
        date: row?.date,
        employee_id: row?.employee_id,
      });
      Toaster.success("Regularisation declined");
      await fetchRows();
    } catch (e) {
      Toaster.error("Failed to decline regularisation");
    }
  };

  const headerPairs = useMemo(() => maxPairsForView, []);
  const minutesTarget = 9 * 60;

  const renderTimeCell = (iso) => {
    if (!iso) return "—";
    return timeFormatter(iso) || "—";
  };

  const renderStatus = (r) => {
    if (!r?.first_login_at || String(r?.mode || "").toLowerCase() === "leave") {
      return <span className="wh-status muted">—</span>;
    }
    const status = String(r?.status || "").toLowerCase();
    if (status === "regularised")
      return <span className="wh-status ok">Regularised</span>;
    if (status === "closed" || status === "complete")
      return <span className="wh-status ok">Close</span>;
    if (status === "sent_for_regularisation" || status === "regularisation")
      return <span className="wh-status warn">Pending Regularisation</span>;
    if (
      status === "declined" ||
      status === "regularisation_declined" ||
      status === "rejected"
    )
      return <span className="wh-status warn">Declined</span>;
    if (status === "open") return <span className="wh-status open">Open</span>;
    return <span className="wh-status muted">—</span>;
  };

  const showPunchUi = tab === "my";
  const modeOptions = [{ value: "Office" }, { value: "WFH" }];
  const timezoneOptions = WORK_TIMEZONE_OPTIONS.map(({ id, value }) => ({
    id,
    value,
  }));
  const filterModeOptions = [
    { value: "All" },
    { value: "WFH" },
    { value: "Office" },
    { value: "Leave" },
  ];

  return (
    <div className="working-hours-page">
      <div className="working-hours-header">
        <div className="working-hours-title">Working Hours</div>
      </div>

      {(isManager || isAdmin) && (
        <div className="working-hours-tabs">
          <button
            type="button"
            className={`working-hours-tab ${tab === "my" ? "active" : ""}`}
            onClick={() => setTab("my")}
          >
            My Login
          </button>
          <button
            type="button"
            className={`working-hours-tab ${tab === "team" ? "active" : ""}`}
            onClick={() => setTab("team")}
          >
            {isAdmin ? "All Logs" : "My Team Login"}
          </button>
        </div>
      )}

      {showPunchUi && (
        <div className="working-hours-punch-card">
          <div className="working-hours-top-actions">
            <div className={`wh-mode-select ${isLoggedIn ? "disabled" : ""}`}>
              <div>
                <Labelbox
                  type="select"
                  value={mode}
                  changeData={setMode}
                  dropdown={modeOptions}
                />
              </div>
            </div>
            <div
              className={`wh-mode-select ${
                isLoggedIn || punching || loading ? "disabled" : ""
              }`}
            >
              <div>
                <Labelbox
                  type="select"
                  value={workTimezone}
                  changeData={handleWorkTimezoneChange}
                  dropdown={timezoneOptions}
                />
              </div>
            </div>
            <Button
              className="primary"
              text="Login"
              disable={
                loading ||
                punching ||
                isLoggedIn ||
                !canPunch ||
                loginLimitReached ||
                !timezoneReady
              }
              handleClick={() => punch("login")}
            />
            <Button
              className="primary"
              text="Logout"
              disable={loading || punching || !isLoggedIn || !canPunch}
              handleClick={() => setLogoutConfirmOpen(true)}
            />
          </div>
          {loginLimitReached && !isLoggedIn ? (
            <div className="wh-login-limit-warn" role="status">
              You have logged in and logged out 3 times already. Further login
              is not allowed for today.
            </div>
          ) : null}
          {!timezoneReady ? (
            <div className="wh-login-limit-warn" role="status">
              Select your work timezone once. Change it later if your work
              region changes.
            </div>
          ) : null}
          <div
            className={`wh-session-timer ${isLoggedIn ? "running" : "idle"}`}
          >
            <div className="wh-session-timer-label">
              {isLoggedIn ? "Logged in for" : "Session timer"}
            </div>
            <div className="wh-session-timer-value">{sessionElapsedLabel}</div>
            <div className="wh-session-timer-hint">
              {isLoggedIn
                ? "Timer runs until you logout. Next login starts a new timer."
                : "Click Login to start tracking your time."}
            </div>
          </div>
        </div>
      )}

      {logoutConfirmOpen ? (
        <div
          className="logout-modal-overlay"
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="logout-modal-title">Logout</h2>
            <p className="logout-modal-question">
              Are you sure you want to logout?
            </p>
            <div className="logout-modal-buttons">
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-no"
                onClick={() => setLogoutConfirmOpen(false)}
              >
                No
              </button>
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-yes"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  punch("logout");
                }}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="working-hours-card">
        <div className="working-hours-filters">
          <div className="wh-filter">
            <label>From</label>
            <Labelbox
              type="datepicker"
              labelname="Start Date"
              value={range.from}
              minDate="2000-01-01"
              maxDate="2099-12-31"
              changeData={(v) => setRange((p) => ({ ...p, from: v || "" }))}
            />
          </div>
          <div className="wh-filter">
            <label>To</label>
            <Labelbox
              type="datepicker"
              labelname="End Date"
              value={range.to}
              minDate="2000-01-01"
              maxDate="2099-12-31"
              changeData={(v) => setRange((p) => ({ ...p, to: v || "" }))}
            />
          </div>
          {(isManager || isAdmin) && tab === "team" && (
            <div className="wh-filter wh-mode-select">
              <label>Employee</label>
              <Labelbox
                type="select"
                value={employeeFilter}
                changeData={(v) => setEmployeeFilter(v || "")}
                dropdown={employeeFilterOptions}
              />
            </div>
          )}
          <div className="wh-filter wh-mode-select">
            <label>Mode</label>
            <Labelbox
              type="select"
              value={filterMode}
              changeData={setFilterMode}
              dropdown={filterModeOptions}
            />
          </div>
          <div className="wh-filter wh-clear-filter">
            <label>&nbsp;</label>
            <Button
              // variant="outline"
              text="Clear Filter"
              handleClick={clearFilters}
            />
          </div>
        </div>

        <div className="working-hours-table-wrap">
          <table className="working-hours-table">
            <thead>
              <tr>
                {(isManager || isAdmin) && tab === "team" && (
                  <th style={{ width: "18%" }}>Employee</th>
                )}
                <th style={{ width: "12%" }}>Date</th>
                {Array.from({ length: headerPairs }).map((_, idx) => (
                  <React.Fragment key={`pair-${idx}`}>
                    <th>Login {idx + 1}</th>
                    <th>Logout {idx + 1}</th>
                  </React.Fragment>
                ))}
                <th style={{ width: "12%" }}>Mode</th>
                <th style={{ width: "12%" }}>TWH</th>
                <th style={{ width: "18%" }}>Regularise</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={20} className="wh-empty-cell">
                    Loading…
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={20} className="wh-empty-cell">
                    No logs
                  </td>
                </tr>
              ) : (
                filteredRows.map((r) => {
                  const events = Array.isArray(r?.events) ? r.events : [];
                  const status = String(r?.status || "").toLowerCase();
                  const isRegularised = status === "regularised";
                  const workYmd = resolveWorkDateYmd(r);
                  // Event-based TWH: live while logged in today, otherwise through
                  // midnight in the work timezone so an open login is not 00:00.
                  const fromEvents = computeMinutesWorked(events, {
                    workDateYmd: workYmd,
                    nowMs: nowTick,
                    includeLiveOpenSession: true,
                  });
                  const computedMins =
                    events.length > 0
                      ? fromEvents
                      : r?.total_minutes != null
                        ? Number(r.total_minutes)
                        : 0;
                  // Regularised rows always show 9:00 hrs (backend should persist 540).
                  const displayMins = isRegularised
                    ? minutesTarget
                    : computedMins;
                  const twhLabel = formatHHMM(displayMins);
                  const twhOk = isRegularised || computedMins >= minutesTarget;

                  const canSendReg =
                    status === "needs_regularisation" ||
                    status === "open" ||
                    status === "declined" ||
                    status === "regularisation_declined" ||
                    status === "rejected" ||
                    status === "";
                  const sent =
                    status === "sent_for_regularisation" ||
                    status === "regularisation";
                  const canManagerDecide =
                    (isManager || isAdmin) &&
                    tab === "team" &&
                    (status === "sent_for_regularisation" ||
                      status === "regularisation" ||
                      status === "needs_regularisation");

                  let rowClass = "";
                  let twhClass = "wh-twh";
                  if (isRegularised) {
                    rowClass = "wh-row--regularised";
                    twhClass += " ok";
                  } else if (String(r.mode || "").toLowerCase() === "leave") {
                    rowClass = "wh-row--leave";
                    twhClass += " leave";
                  } else if (!r.first_login_at) {
                    rowClass = "wh-row--not-logged-in";
                    twhClass += " not-logged-in";
                  } else if (twhOk) {
                    rowClass = "wh-row--completed";
                    twhClass += " ok";
                  } else {
                    rowClass = "wh-row--logged-in";
                    twhClass += " warn";
                  }

                  return (
                    <tr
                      key={`${r.employee_id || "me"}-${r.date}`}
                      className={rowClass}
                    >
                      {(isManager || isAdmin) && tab === "team" && (
                        <td className="wh-employee">
                          {r.employee_name || r.employee_email || "—"}
                        </td>
                      )}
                      <td>{r.date || "—"}</td>
                      {Array.from({ length: headerPairs }).map((_, idx) => {
                        const pairs = pairLoginLogout(events);
                        const loginEvent = pairs[idx]?.login;
                        const logoutEvent = pairs[idx]?.logout;
                        return (
                          <React.Fragment key={`cells-${idx}`}>
                            <td>{renderTimeCell(loginEvent?.at)}</td>
                            <td>{renderTimeCell(logoutEvent?.at)}</td>
                          </React.Fragment>
                        );
                      })}
                      <td>
                        <span className="wh-mode">{r.mode || "—"}</span>
                      </td>
                      <td>
                        <span className={twhClass}>{twhLabel}</span>
                      </td>
                      <td className="wh-actions">
                        {canManagerDecide ? (
                          <div className="wh-reg-actions">
                            <button
                              type="button"
                              className="wh-reg-icon wh-reg-icon--accept"
                              aria-label="Accept regularisation"
                              title="Accept"
                              onClick={() => regulariseRow(r)}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              className="wh-reg-icon wh-reg-icon--decline"
                              aria-label="Decline regularisation"
                              title="Decline"
                              onClick={() => declineRegularisation(r)}
                            >
                              ✕
                            </button>
                          </div>
                        ) : sent ? (
                          renderStatus(r)
                        ) : !twhOk && canSendReg && tab === "my" && !isAdmin ? (
                          <Button
                            variant="outline"
                            text="Send for regularisation"
                            handleClick={() => sendForRegularisation(r)}
                          />
                        ) : (
                          renderStatus(r)
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WorkingHours;
